import { useEffect, useState, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export const isStandalone = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    // iOS Safari
    (window.navigator as any).standalone === true
  );
};

export const isDesktop = (): boolean => {
  if (typeof window === "undefined") return false;
  // Use pointer fineness rather than viewport width — a narrow desktop
  // window is still a desktop, and a tablet in landscape is still a tablet.
  return window.matchMedia("(pointer: fine)").matches;
};

export const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

export const isSafari = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  // Safari UA contains "Safari" but not "Chrome" / "Chromium" / "Edg" / "FxiOS" / "CriOS"
  return (
    /Safari/.test(ua) &&
    !/Chrome|Chromium|Edg|CriOS|FxiOS|OPR/.test(ua)
  );
};

export const isMac = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /Mac/.test(navigator.platform) || /Macintosh/.test(navigator.userAgent);
};

const DISMISSED_KEY = "requestapp.installPromptDismissedAt";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(DISMISSED_KEY);
      if (!v) return false;
      return Date.now() - Number(v) < DISMISS_COOLDOWN_MS;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const handler = () => setInstalled(true);
    window.addEventListener("appinstalled", handler);
    return () => window.removeEventListener("appinstalled", handler);
  }, []);

  const promptInstall = useCallback(async (): Promise<"accepted" | "dismissed" | "unavailable"> => {
    if (!deferredPrompt) return "unavailable";
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (outcome === "accepted") setInstalled(true);
    return outcome;
  }, [deferredPrompt]);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {}
    setDismissed(true);
  }, []);

  return {
    canInstall: !!deferredPrompt,
    installed,
    dismissed,
    isStandalone: installed,
    isDesktop: isDesktop(),
    isSafari: isSafari(),
    isIOS: isIOS(),
    isMac: isMac(),
    promptInstall,
    dismiss,
  };
};
